import { useId, useState, type ReactNode } from "react";
import type { UseQueryResult } from "@tanstack/react-query";
import { ArrowLeft, Loader2, Undo2 } from "lucide-react";
import { Link, useParams } from "react-router";
import {
  FILE_STATUS_LABELS,
  IMPORT_STATUS_LABELS,
  STATUS_DOUBT_LABELS,
  type DocumentQuestion,
  type FileStatus,
  type DocumentLink,
  type ProjectChange,
  type ProjectDocument,
  type ProjectImport,
  type QuestionMember,
} from "@gb/contracts";
import { ContentColumn } from "@/app/app-shell";
import { useProjectContext } from "@/app/project-context";
import { Button } from "@/components/ui/button";
import { DocumentStatus } from "@/features/brain/document-status";
import { FileLink, type CameFrom } from "@/features/brain/file-link";
import { roleSentence } from "@/features/brain/family-words";
import { PlacementQuestion } from "@/features/brain/placement-question";
import { LinkQuestion } from "@/features/brain/document-links";
import { useAskedLinks, useSetLinkState } from "@/features/brain/links-api";
import { ImportDebug } from "@/features/debug/debug-panels";
import {
  useDeleteDocument,
  useDocuments,
  useImport,
  usePlaceDocument,
  useSetDocumentStatuses,
  useUndoChange,
} from "@/features/project/documents-api";
import { formatDayOf } from "@/lib/format";
import { reviewFacts, files, stillOpen, type ReviewFacts } from "./review-facts";
import { reviewPath, useChanges, useReviewImport } from "./review-api";

/**
 * The review after an import (Phase 3f, `document-ai/0017` §9, answered by the
 * product owner 2026-09-22).
 *
 * **A screen of its own, reached by a banner.** An import arrives during setup
 * and long after it, so the wizard is the wrong home; the Brain's shelves stay
 * the standing view of everything a project holds, and this is one bundle at
 * one moment.
 *
 * **A person acts here.** Answering a version question, keeping or removing a
 * flagged file and undoing a placement all happen without leaving. A real
 * bundle was 26 files, and reviewing it must not mean opening 26 pages. The
 * same controls stay on each file's own screen, and the two share components
 * rather than copies.
 *
 * The sections run in `0019` §2's order — what arrived, versions, flags —
 * then the files nothing could name, then the log. **The outline's section
 * went on 2026-10-03** (`document-ai/0032`): a file's category comes from its
 * card and is on the Sources tab's shelves, and there is no outline to fix.
 * **Every section says so when it is empty.** Rendering only what has a count
 * reads as "this did not run" rather than "there was nothing".
 */
export function ImportReviewScreen() {
  const { importId } = useParams();
  const { projectId } = useProjectContext();
  const current = useImport(projectId, importId);
  const documents = useDocuments(projectId);

  if (current.isPending || documents.isPending) {
    return (
      <ContentColumn>
        <div className="flex items-center gap-2 py-16 text-muted" role="status" aria-live="polite">
          <Loader2 size={16} className="animate-spin" />
          <span className="text-[0.9rem]">Opening the import…</span>
        </div>
      </ContentColumn>
    );
  }

  if (current.isError || documents.isError || !importId) {
    const error = current.error ?? documents.error;
    return (
      <ContentColumn>
        <BackToBrain projectId={projectId} />
        <div className="mt-4 rounded-card-lg border-[1.5px] border-danger/40 bg-danger-bg px-6 py-5">
          <p className="text-[0.92rem] font-semibold text-danger">Couldn’t open this import.</p>
          <p className="mt-1 text-[0.85rem] text-ink-slate">
            {error instanceof Error ? error.message : "It may belong to another project."}
          </p>
        </div>
      </ContentColumn>
    );
  }

  const bundle = current.data;
  const facts = reviewFacts(bundle.id, documents.data);
  const from: CameFrom = { path: reviewPath(projectId, bundle.id), label: "Back to the review" };
  const settled = bundle.status === "review" || bundle.status === "done";

  return (
    <ContentColumn tight>
      <BackToBrain projectId={projectId} />
      <Header projectId={projectId} bundle={bundle} facts={facts} />

      {!settled ? (
        <NotReady bundle={bundle} />
      ) : (
        <>
          <Section title="What arrived">
            <Arrived projectId={projectId} bundle={bundle} facts={facts} />
          </Section>

          <Section title="Versions">
            <Versions projectId={projectId} facts={facts} from={from} />
          </Section>

          <Section title="Links to confirm">
            <AskedLinks projectId={projectId} importId={bundle.id} from={from} />
          </Section>

          <Section title="Flagged files">
            <Flags projectId={projectId} facts={facts} from={from} />
          </Section>

          <Section title="Files nothing could name">
            <Unread projectId={projectId} facts={facts} from={from} />
          </Section>

          <Section title="What changed">
            <Changes projectId={projectId} importId={bundle.id} />
          </Section>

          <MarkReviewed projectId={projectId} bundle={bundle} facts={facts} />

          {/* The import's own steps and model calls, with Debug on. */}
          <ImportDebug projectId={projectId} importId={bundle.id} />
        </>
      )}
    </ContentColumn>
  );
}

/**
 * The links this bundle's reading was not sure of (`document-ai/0024` §8.5).
 * A link that applied itself is in the log below and on each file's page; only
 * the ones waiting for an answer are here, as a placement's question is.
 */
function AskedLinks({ projectId, importId, from }: { projectId: string; importId: string; from: CameFrom }) {
  return <AskedLinksList projectId={projectId} asked={useAskedLinks(projectId, importId)} from={from} />;
}

/** The links of one import, or of the whole project, waiting for an answer. */
export function AskedLinksList({
  projectId,
  asked,
  from,
}: {
  projectId: string;
  asked: UseQueryResult<DocumentLink[]>;
  from: CameFrom;
}) {
  const setState = useSetLinkState(projectId);
  if (asked.isPending) {
    return (
      <p className="flex items-center gap-2 text-[0.85rem] text-muted" role="status">
        <Loader2 size={14} className="animate-spin" /> Reading the links…
      </p>
    );
  }
  if (asked.isError) {
    return (
      <p role="alert" className="text-[0.85rem] font-semibold text-danger">
        {asked.error.message}
      </p>
    );
  }
  if (asked.data.length === 0) return <Nothing>No link is waiting for an answer.</Nothing>;
  return (
    <div className="flex flex-col gap-2">
      {asked.data.map((link) => (
        <LinkQuestion
          key={link.id}
          projectId={projectId}
          link={link}
          from={from}
          busy={setState.isPending && setState.variables?.linkId === link.id}
          onAnswer={(yes) => setState.mutate({ linkId: link.id!, state: yes ? "live" : "removed" })}
        />
      ))}
      {setState.error && (
        <p role="alert" className="text-[0.85rem] font-semibold text-danger">
          {setState.error.message}
        </p>
      )}
    </div>
  );
}

export function BackToBrain({ projectId }: { projectId: string }) {
  return (
    <Link
      to={`/projects/${projectId}/brain/sources`}
      className="inline-flex items-center gap-1.5 text-[0.85rem] font-semibold text-muted transition-colors hover:text-ink"
    >
      <ArrowLeft size={15} />
      Back to the Brain
    </Link>
  );
}

export function Section({ title, children }: { title: string; children: ReactNode }) {
  const id = useId();
  return (
    <section aria-labelledby={id} className="mt-7">
      <h2
        id={id}
        className="mb-2.5 font-display text-[0.7rem] font-semibold tracking-[0.12em] text-muted uppercase"
      >
        {title}
      </h2>
      {children}
    </section>
  );
}

/** Nothing to show in a section, said as a fact rather than left blank. */
export function Nothing({ children }: { children: ReactNode }) {
  return <p className="text-[0.88rem] text-muted">{children}</p>;
}

/**
 * Where each kind of import came from, as its heading says it. One entry per
 * source, so a new source cannot fall back to "uploaded from this device"
 * unseen, as SharePoint's and Outlook's did (2026-10-18, QA finding 3).
 */
const FROM: Record<ProjectImport["source"], string> = {
  local: "uploaded from this device",
  "google-drive": "from Google Drive",
  sharepoint: "from SharePoint",
  outlook: "from Outlook",
};

/** What the import's heading says it holds. */
function heading(bundle: ProjectImport): string {
  // The programme from setup is an import of its own (2026-10-03).
  if (bundle.programme) return "The programme, from setup";
  return `${files(bundle.files)} ${FROM[bundle.source]}`;
}

function Header({
  projectId,
  bundle,
  facts,
}: {
  projectId: string;
  bundle: ProjectImport;
  facts: ReviewFacts;
}) {
  return (
    <header className="mt-4 mb-2 flex flex-wrap items-start justify-between gap-4">
      <div className="min-w-0">
        <p className="font-display text-[0.7rem] font-semibold tracking-[0.12em] text-brand uppercase">
          Import · <time dateTime={bundle.openedAt}>{formatDayOf(bundle.openedAt)}</time>
        </p>
        <h1 className="mt-1 font-display text-[1.55rem] leading-[1.1] font-bold tracking-[0.01em] text-ink uppercase">
          {heading(bundle)}
        </h1>
        <p className="mt-1.5 text-[0.88rem] text-ink-slate">
          {IMPORT_STATUS_LABELS[bundle.status]}
          {bundle.reviewedAt && (
            <>
              {" on "}
              <time dateTime={bundle.reviewedAt}>{formatDayOf(bundle.reviewedAt)}</time>
            </>
          )}
          .
        </p>
      </div>
      {bundle.status === "review" && (
        <ReviewButton projectId={projectId} bundle={bundle} facts={facts} compact />
      )}
    </header>
  );
}

/** Still being read or organised, or never organised. */
function NotReady({ bundle }: { bundle: ProjectImport }) {
  if (bundle.status === "failed") {
    return (
      <div className="mt-5 rounded-card-lg border-[1.5px] border-danger/40 bg-danger-bg px-5 py-4">
        <p className="text-[0.92rem] font-semibold text-danger">
          This import could not be organised.
        </p>
        <p className="mt-1 text-[0.85rem] text-ink-slate">
          Its files are stored and can be read. The project organises them on its own instead, or
          at once with “Organise now” on the Sources tab.
        </p>
      </div>
    );
  }
  return (
    <div
      className="mt-5 flex items-center gap-2 rounded-card-lg border-[1.5px] border-border bg-surface px-5 py-4 text-[0.9rem] text-ink-slate"
      role="status"
      aria-live="polite"
    >
      <Loader2 size={16} className="animate-spin text-brand" aria-hidden />
      {bundle.read} of {files(bundle.files + bundle.unpacked)} read so far. There is nothing to review
      until every file has been read and organised. This page fills in by itself.
    </div>
  );
}

/**
 * What arrived, each count in its own unit (2026-09-25, `document-ai/0017`
 * §14). `files` is what a person uploaded or picked. What was unpacked from a
 * zip or an email is said on its own line, so 229 uploads do not read as 239,
 * and "read" is counted over both.
 */
function arrivedLines(bundle: ProjectImport, facts: ReviewFacts): string[] {
  const copies = facts.files.filter((document) => document.family.role === "identical-copy");
  const total = bundle.files + bundle.unpacked;
  const unpackedFrom = parentsOf(facts.files);
  const read =
    bundle.unpacked === 0
      ? [`${files(bundle.files)} arrived, and ${bundle.read} ${bundle.read === 1 ? "was" : "were"} read.`]
      : [
          `${files(bundle.files)} arrived.`,
          `${bundle.unpacked} more ${bundle.unpacked === 1 ? "file was" : "files were"} unpacked from ${
            bundle.files === 1 && unpackedFrom <= 1 ? "it" : `${Math.max(1, unpackedFrom)} of them`
          }.`,
          `${bundle.read} of the ${total} ${bundle.read === 1 ? "was" : "were"} read.`,
        ];
  return [
    ...read,
    copies.length > 0
      ? `${files(copies.length)} ${copies.length === 1 ? "was an exact copy" : "were exact copies"} of another, and ${copies.length === 1 ? "was" : "were"} folded into ${copies.length === 1 ? "it" : "them"} rather than read twice.`
      : null,
    bundle.failed > 0 ? `${files(bundle.failed)} could not be read.` : null,
    bundle.refused > 0
      ? `${files(bundle.refused)} ${bundle.refused === 1 ? "was" : "were"} refused before ${bundle.refused === 1 ? "it" : "they"} became a document.`
      : null,
    bundle.unchanged > 0
      ? `${files(bundle.unchanged)} ${bundle.unchanged === 1 ? "was" : "were"} already here, unchanged, and not stored again.`
      : null,
  ].filter((line): line is string => line !== null);
}

/**
 * How many of the uploaded files something was unpacked from. A file unpacked
 * from a file that was itself unpacked — a zip inside an email — counts under
 * the one that was uploaded.
 */
function parentsOf(documents: readonly ProjectDocument[]): number {
  const parentOf = new Map(documents.map((document) => [document.id, document.parentDocumentId]));
  const top = new Set<string>();
  for (const document of documents) {
    if (!document.parentDocumentId) continue;
    let id = document.parentDocumentId;
    for (let depth = 0; depth < 10 && parentOf.get(id); depth += 1) id = parentOf.get(id)!;
    top.add(id);
  }
  return top.size;
}

function Arrived({
  projectId,
  bundle,
  facts,
}: {
  projectId: string;
  bundle: ProjectImport;
  facts: ReviewFacts;
}) {
  const lines = arrivedLines(bundle, facts);

  return (
    <div className="rounded-card-lg border-[1.5px] border-border bg-surface px-4 py-3">
      <ul className="flex flex-col gap-1 text-[0.88rem] text-ink">
        {lines.map((line) => (
          <li key={line}>{line}</li>
        ))}
      </ul>
      {bundle.failures.length > 0 && (
        <>
          <p className="mt-3 text-[0.8rem] font-semibold text-ink-slate">Never arrived</p>
          <ul className="mt-1 flex flex-col gap-1">
            {bundle.failures.map((failure, index) => (
              <li key={`${failure.name}-${index}`} className="text-[0.82rem] text-muted">
                <span className="font-semibold text-ink">{failure.name}</span> — {failure.reason}
                {failure.documentId && (
                  <RemoveUnfinished
                    projectId={projectId}
                    documentId={failure.documentId}
                    name={failure.name}
                  />
                )}
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

/**
 * Remove an upload that never finished (`document-ai/0019` §8, decided by the
 * product owner 2026-09-25).
 *
 * Only an unfinished upload carries an id on this list: a failed Drive fetch
 * left nothing to remove. It was never a file, so removing it writes no line
 * in the log. The import is re-read afterwards, and the line goes.
 */
function RemoveUnfinished({
  projectId,
  documentId,
  name,
}: {
  projectId: string;
  documentId: string;
  name: string;
}) {
  const remove = useDeleteDocument(projectId);
  return (
    <>
      {" "}
      <Button
        variant="secondary"
        size="sm"
        className="ml-1 align-middle"
        disabled={remove.isPending}
        aria-label={`Remove ${name}`}
        onClick={() => remove.mutate(documentId)}
      >
        {remove.isPending ? <Loader2 size={14} className="animate-spin" /> : null}
        Remove
      </Button>
      {remove.error && (
        <span role="alert" className="ml-2 font-semibold text-danger">
          {remove.error.message}
        </span>
      )}
    </>
  );
}

/**
 * A file's name on this screen: its card's title, or else its whole filename.
 * The extension stays when there is no title, because a review lists files
 * side by side, and `Anschluskabel.pdf` and `Anschluskabel.xlsx` both
 * read "Anschluskabel" without it (found on the Austrian import).
 */
function nameOf(document: Pick<ProjectDocument, "title" | "filename">): string {
  return document.title ?? document.filename;
}

// ------------------------------------------------------------------
// Versions
// ------------------------------------------------------------------

export function Versions({
  projectId,
  facts,
  from,
}: {
  projectId: string;
  facts: Pick<ReviewFacts, "asking" | "placed">;
  from: CameFrom;
}) {
  // Answered here, by id, with what was said. A question answered on this
  // screen leaves the list the moment the file is re-read, and the answer has
  // to stay said where the person pressed it.
  const [answered, setAnswered] = useState<Map<string, { name: string; said: string }>>(
    new Map(),
  );
  const open = facts.asking.filter((document) => !answered.has(document.id));
  // One question per proposed family (VER-08, 2026-09-26): the files asked in
  // one family are one block, and a person answers each file inside it.
  const questions = new Map<string, DocumentQuestion>();
  for (const document of open) {
    const question = document.question!;
    if (questions.has(question.id)) continue;
    const members = question.members.filter((member) => !answered.has(member.document.id));
    if (members.length > 0) questions.set(question.id, { ...question, members });
  }
  // What was worked out without asking: an older version, another format. A
  // copy is said under "What arrived".
  const worked = facts.placed.filter(
    (document) => document.family.role !== "identical-copy" && document.question === null,
  );

  return (
    <div className="flex flex-col gap-3">
      {questions.size === 0 && answered.size === 0 ? (
        <Nothing>Nothing here needs a decision.</Nothing>
      ) : (
        <ul className="flex flex-col gap-3">
          {[...questions.values()].map((question) => (
            <li key={question.id}>
              <QuestionBlock
                projectId={projectId}
                question={question}
                from={from}
                onAnswered={(member, said) =>
                  setAnswered((before) => new Map(before).set(member.document.id, { name: member.document.name, said }))
                }
              />
            </li>
          ))}
          {[...answered.entries()].map(([id, { name, said }]) => (
            <li key={id} className="rounded-card-lg border-[1.5px] border-border bg-surface px-4 py-3">
              <FileLink projectId={projectId} id={id} from={from}>
                {name}
              </FileLink>
              <p role="status" className="mt-1 text-[0.85rem] font-semibold text-ok">
                {said}
              </p>
            </li>
          ))}
        </ul>
      )}

      {worked.length > 0 && (
        <div className="rounded-card-lg border-[1.5px] border-border bg-surface px-4 py-3">
          <p className="text-[0.8rem] font-semibold text-ink-slate">Placed among their versions</p>
          <ul className="mt-1.5 flex flex-col gap-1">
            {worked.map((document) => (
              <li key={document.id} className="text-[0.85rem] text-ink">
                <FileLink projectId={projectId} id={document.id} from={from}>
                  {nameOf(document)}
                </FileLink>
                <span className="text-muted">
                  {" · "}
                  {roleSentence(document.family.role, document.family.related?.name ?? null)}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

/**
 * One family's question: every file of it that is still asked, each answered
 * on its own (VER-08, 2026-09-26). A question about one file names the file
 * above it and reads "this file", as before.
 */
function QuestionBlock({
  projectId,
  question,
  from,
  onAnswered,
}: {
  projectId: string;
  question: DocumentQuestion;
  from: CameFrom;
  onAnswered: (member: QuestionMember, said: string) => void;
}) {
  const answer = usePlaceDocument(projectId);
  const only = question.members.length === 1 ? question.members[0]! : null;
  return (
    <>
      <PlacementQuestion
        projectId={projectId}
        question={question}
        self={only?.document.id ?? null}
        busy={answer.isPending}
        from={from}
        heading="Needs a decision"
        lead={
          only ? (
            <p className="mt-1 text-[0.85rem] text-ink-slate">
              <FileLink projectId={projectId} id={only.document.id} from={from}>
                {only.document.name}
              </FileLink>
            </p>
          ) : undefined
        }
        onAnswer={(member, yes) =>
          answer.mutate(
            {
              documentId: member.document.id,
              placement: yes
                ? { relation: member.relation, documentId: member.other.id }
                : { relation: "on-its-own" },
            },
            {
              onSuccess: () =>
                onAnswered(
                  member,
                  yes
                    ? "Saved. A later import won’t move it."
                    : "Saved as on its own. A later import won’t move it.",
                ),
            },
          )
        }
      />
      {answer.error && (
        <p role="alert" className="mt-2 text-[0.85rem] font-semibold text-danger">
          {answer.error.message}
        </p>
      )}
    </>
  );
}

// ------------------------------------------------------------------
// Flags
// ------------------------------------------------------------------

/**
 * The files the check hid, and the ones it asked about (HID-03, 2026-09-26).
 *
 * **The questions come first.** A file the check would have hidden but did
 * not is still used in answers, and a wrong hide among them is what a person
 * most needs to find. In the German set the one wrong other-project hide was one
 * of 70 in a single list, with nothing to tell it apart. Each question says
 * why it was asked, in a few words beside the name and in full below.
 *
 * **A group can be answered at once.** At 5,000 files a project can hold
 * about 1,850 flags (projected), and nobody reads 1,850 reasons one by one.
 * A group answer asks before it acts, since it changes every file in the
 * group; there is no Remove for a group, which would delete them.
 */
export function Flags({
  projectId,
  facts,
  from,
}: {
  projectId: string;
  facts: Pick<ReviewFacts, "hidden" | "unsure" | "kept">;
  from: CameFrom;
}) {
  // A removed file is gone from the list, so what was removed is said from
  // here, and so is what a group answer did: its group is gone too. A kept
  // one stays in `facts.kept`, and its block says so itself.
  const [removed, setRemoved] = useState<string[]>([]);
  const [said, setSaid] = useState<string[]>([]);
  const asked = groupBy(facts.unsure, (document) => document.status.question!.status);
  const hidden = groupBy(facts.hidden, (document) => document.status.status!);

  if (
    facts.hidden.length === 0 &&
    facts.unsure.length === 0 &&
    facts.kept.length === 0 &&
    removed.length === 0 &&
    said.length === 0
  ) {
    return (
      <Nothing>
        The check found nothing here that belongs to another project, or is a template, private
        or junk.
      </Nothing>
    );
  }

  const done = (sentence: string) => setSaid((before) => [...before, sentence]);
  return (
    <div className="flex flex-col gap-4">
      {[...asked.entries()].map(([status, group]) => (
        <FlagGroup
          key={`asked-${status}`}
          heading={`${FILE_STATUS_LABELS[status]}? · ${files(group.length)}`}
          lead={`The check would have hidden ${group.length === 1 ? "this file" : "these files"}, but it was not sure. ${group.length === 1 ? "It is" : "They are"} used in answers until someone decides.`}
          answers={
            // One file is answered on its own block.
            group.length > 1
              ? [
                  { label: `Hide all ${group.length}`, status, said: `Hidden: ${files(group.length)}.` },
                  { label: `Keep all ${group.length}`, status: "this-project", said: `Kept: ${files(group.length)}.` },
                ]
              : []
          }
          projectId={projectId}
          group={group}
          onDone={done}
        >
          {group.map((document) => (
            <li key={document.id}>
              <FileLink projectId={projectId} id={document.id} from={from}>
                {nameOf(document)}
              </FileLink>
              <DoubtMarkers document={document} />
              {/* Remove asks twice here and names the doubt (HID-12). The
                  review stays up, and says what went. */}
              <DocumentStatus
                projectId={projectId}
                document={document}
                from={from}
                onRemoved={() => setRemoved((before) => [...before, nameOf(document)])}
              />
            </li>
          ))}
        </FlagGroup>
      ))}
      {[...hidden.entries()].map(([status, group]) => (
        <FlagGroup
          key={status}
          heading={`${FILE_STATUS_LABELS[status]} · ${files(group.length)}`}
          answers={
            group.length > 1
              ? [{ label: `Keep all ${group.length}`, status: "this-project", said: `Kept: ${files(group.length)}.` }]
              : []
          }
          projectId={projectId}
          group={group}
          onDone={done}
        >
          {group.map((document) => (
            <li key={document.id}>
              <FileLink projectId={projectId} id={document.id} from={from}>
                {nameOf(document)}
              </FileLink>
              <DocumentStatus
                projectId={projectId}
                document={document}
                from={from}
                onRemoved={() => setRemoved((before) => [...before, nameOf(document)])}
              />
            </li>
          ))}
        </FlagGroup>
      ))}
      {(facts.kept.length > 0 || removed.length > 0 || said.length > 0) && (
        <ul className="flex flex-col gap-1 rounded-card-lg border-[1.5px] border-border bg-surface px-4 py-3">
          {said.map((sentence, index) => (
            <li key={`said-${index}`} role="status" className="text-[0.85rem] font-semibold text-ok">
              {sentence}
            </li>
          ))}
          {facts.kept.map((document) => (
            <li key={document.id} className="text-[0.85rem] text-ink">
              <FileLink projectId={projectId} id={document.id} from={from}>
                {nameOf(document)}
              </FileLink>
              <span className="text-muted"> · Kept by a person, and used in answers.</span>
            </li>
          ))}
          {removed.map((name, index) => (
            <li key={`${name}-${index}`} className="text-[0.85rem] text-ink">
              <span className="font-semibold">{name}</span>
              <span className="text-muted"> · Removed.</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** Files grouped by a key, in the order they came: upload order within each group. */
function groupBy<K>(documents: readonly ProjectDocument[], keyOf: (document: ProjectDocument) => K): Map<K, ProjectDocument[]> {
  const groups = new Map<K, ProjectDocument[]>();
  for (const document of documents) {
    const key = keyOf(document);
    groups.set(key, [...(groups.get(key) ?? []), document]);
  }
  return groups;
}

/** Why the check asked, in a few words beside the file's name. */
function DoubtMarkers({ document }: { document: ProjectDocument }) {
  const kinds = [...new Set((document.status.question?.doubts ?? []).map((doubt) => doubt.kind))];
  if (kinds.length === 0) return null;
  return (
    <span className="text-[0.8rem] font-semibold text-warn-text">
      {" · "}
      {kinds.map((kind) => STATUS_DOUBT_LABELS[kind]).join(" · ")}
    </span>
  );
}

/**
 * One group of flags, with the answers that act on the whole group. An answer
 * asks "Keep all 70?" first, since it changes every file in the group.
 */
function FlagGroup({
  heading,
  lead,
  answers,
  projectId,
  group,
  onDone,
  children,
}: {
  heading: string;
  lead?: string;
  answers: { label: string; status: FileStatus; said: string }[];
  projectId: string;
  group: ProjectDocument[];
  onDone: (sentence: string) => void;
  children: ReactNode;
}) {
  const [confirming, setConfirming] = useState<number | null>(null);
  const answer = useSetDocumentStatuses(projectId);
  const chosen = confirming === null ? null : answers[confirming]!;
  return (
    <div>
      <div className="mb-1.5 flex flex-wrap items-center justify-between gap-2">
        <p className="text-[0.8rem] font-semibold text-ink-slate">{heading}</p>
        {answers.length > 0 &&
          (chosen ? (
            <span className="flex items-center gap-1.5">
              <span className="text-[0.78rem] font-semibold text-ink">{chosen.label}?</span>
              <Button variant="secondary" size="sm" disabled={answer.isPending} onClick={() => setConfirming(null)}>
                No
              </Button>
              <Button
                size="sm"
                className="hover:bg-brand-deep"
                disabled={answer.isPending}
                onClick={() =>
                  answer.mutate(
                    { documentIds: group.map((document) => document.id), status: chosen.status },
                    {
                      onSuccess: () => onDone(chosen.said),
                      onSettled: () => setConfirming(null),
                    },
                  )
                }
              >
                {answer.isPending ? <Loader2 size={14} className="animate-spin" /> : null}
                Yes
              </Button>
            </span>
          ) : (
            <span className="flex items-center gap-1.5">
              {answers.map((each, index) => (
                <Button
                  key={each.label}
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    answer.reset();
                    setConfirming(index);
                  }}
                >
                  {each.label}
                </Button>
              ))}
            </span>
          ))}
      </div>
      {lead && <p className="mb-2 text-[0.82rem] leading-[1.5] text-ink-slate">{lead}</p>}
      {answer.error && (
        <p role="alert" className="mb-2 text-[0.85rem] font-semibold text-danger">
          {answer.error.message}
        </p>
      )}
      <ul className="flex flex-col gap-3">{children}</ul>
    </div>
  );
}

// ------------------------------------------------------------------
// Files nothing could name
// ------------------------------------------------------------------

function Unread({
  projectId,
  facts,
  from,
}: {
  projectId: string;
  facts: ReviewFacts;
  from: CameFrom;
}) {
  if (facts.unread.length === 0) {
    return <Nothing>Every file here was read well enough to say what it is.</Nothing>;
  }
  return (
    <div className="rounded-card-lg border-[1.5px] border-border bg-surface px-4 py-3">
      <p className="text-[0.85rem] text-ink-slate">
        Nothing could tell what {facts.unread.length === 1 ? "this file is" : "these files are"}{" "}
        from what was read in {facts.unread.length === 1 ? "it" : "them"}. {facts.unread.length === 1 ? "It is" : "They are"}{" "}
        stored. Open one to look at it.
      </p>
      <ul className="mt-2 flex flex-col gap-1.5">
        {facts.unread.map((document) => (
          <li key={document.id} className="text-[0.85rem] text-ink">
            <FileLink projectId={projectId} id={document.id} from={from}>
              {nameOf(document)}
            </FileLink>
            {document.card.missingBecause && (
              <span className="text-muted"> · {document.card.missingBecause}</span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

// ------------------------------------------------------------------
// What changed
// ------------------------------------------------------------------

const MADE_BY_WORDS: Record<ProjectChange["madeBy"], string> = {
  model: "The model",
  code: "Found by code",
  person: "A person",
};

function Changes({ projectId, importId }: { projectId: string; importId: string }) {
  const changes = useChanges(projectId, importId);
  if (changes.isPending) {
    return (
      <p className="flex items-center gap-2 text-[0.85rem] text-muted" role="status">
        <Loader2 size={14} className="animate-spin" /> Reading the log…
      </p>
    );
  }
  if (changes.isError) {
    return (
      <p role="alert" className="text-[0.85rem] font-semibold text-danger">
        {changes.error.message}
      </p>
    );
  }
  if (changes.data.length === 0) {
    return <Nothing>Nothing has been changed in this import yet.</Nothing>;
  }
  return (
    <ul className="flex flex-col divide-y divide-border rounded-card-lg border-[1.5px] border-border bg-surface">
      {changes.data.map((change) => (
        <ChangeRow key={change.id} projectId={projectId} change={change} />
      ))}
    </ul>
  );
}

function ChangeRow({ projectId, change }: { projectId: string; change: ProjectChange }) {
  const [confirming, setConfirming] = useState(false);
  const undo = useUndoChange(projectId);
  return (
    <li className="flex flex-wrap items-start justify-between gap-3 px-4 py-2.5">
      <div className="min-w-0">
        <p className={change.undoneById ? "text-[0.85rem] text-muted line-through" : "text-[0.85rem] text-ink"}>
          {change.summary}
        </p>
        <p className="mt-0.5 text-[0.76rem] text-muted">
          {MADE_BY_WORDS[change.madeBy]} ·{" "}
          <time dateTime={change.createdAt}>{formatDayOf(change.createdAt)}</time>
          {change.undoneById ? " · Undone" : ""}
        </p>
        {undo.error && (
          <p role="alert" className="mt-1 text-[0.8rem] font-semibold text-danger">
            {undo.error.message}
          </p>
        )}
      </div>
      {change.canUndo &&
        (confirming ? (
          <span className="flex items-center gap-1.5">
            <span className="text-[0.78rem] font-semibold text-ink">Undo this?</span>
            <Button
              variant="secondary"
              size="sm"
              disabled={undo.isPending}
              onClick={() => setConfirming(false)}
            >
              No
            </Button>
            <Button
              size="sm"
              className="hover:bg-brand-deep"
              disabled={undo.isPending}
              onClick={() =>
                undo.mutate(change.id, { onSettled: () => setConfirming(false) })
              }
            >
              Yes
            </Button>
          </span>
        ) : (
          <Button variant="secondary" size="sm" onClick={() => setConfirming(true)}>
            <Undo2 size={14} />
            Undo
          </Button>
        ))}
    </li>
  );
}

// ------------------------------------------------------------------
// Mark reviewed
// ------------------------------------------------------------------

function MarkReviewed({
  projectId,
  bundle,
  facts,
}: {
  projectId: string;
  bundle: ProjectImport;
  facts: ReviewFacts;
}) {
  if (bundle.status !== "review") {
    return (
      <p className="mt-8 text-[0.88rem] text-muted">
        {bundle.reviewedAt
          ? `Reviewed on ${formatDayOf(bundle.reviewedAt)}. Anything above can still be changed.`
          : IMPORT_STATUS_LABELS[bundle.status]}
      </p>
    );
  }
  return (
    <div className="mt-8 border-t border-border pt-5">
      <ReviewButton projectId={projectId} bundle={bundle} facts={facts} />
    </div>
  );
}

/**
 * Mark it reviewed. **Not gated on the open questions**: a review says a person
 * looked, and a question left open stays where it asks. What is still open is
 * said beside the button, so pressing it is a choice.
 */
function ReviewButton({
  projectId,
  bundle,
  facts,
  compact = false,
}: {
  projectId: string;
  bundle: ProjectImport;
  facts: ReviewFacts;
  compact?: boolean;
}) {
  const review = useReviewImport(projectId);
  // The same query the Links section reads: an extra observer, not a fetch.
  const asked = useAskedLinks(projectId, bundle.id);
  const left = stillOpen({
    asking: facts.asking.length,
    links: asked.data?.length ?? 0,
    hidden: facts.hidden.length,
    unsure: facts.unsure.length,
  });

  return (
    <div className={compact ? "flex flex-col items-end gap-1.5" : "flex flex-col gap-2"}>
      <Button
        className="hover:bg-brand-deep"
        disabled={review.isPending}
        onClick={() => review.mutate(bundle.id)}
      >
        {review.isPending ? <Loader2 size={16} className="animate-spin" /> : null}
        Mark reviewed
      </Button>
      {!compact && left && <p className="text-[0.82rem] text-ink-slate">{left}</p>}
      {review.error && (
        <p role="alert" className="text-[0.85rem] font-semibold text-danger">
          {review.error.message}
        </p>
      )}
    </div>
  );
}
